import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  LabelList
} from "recharts";
import { DisaggregatedMetric, GeoDetails, GeoTag } from "../../services/types";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { formatValue, formatValueBase } from "../../utils/helpers";
import MetricMetadataTrigger from "../metrics/MetricMetadataTrigger";
import { useMemo } from "react";

interface RaceEthnicityBarChartCardProps {
    raceMetric: DisaggregatedMetric | undefined;
    disaggData: DisaggregatedMetric[] | null;
    geoDetails: GeoDetails[];
    geoTag: GeoTag | null;
    selectionActive: boolean;
    ethnicityMetric?: DisaggregatedMetric;
    barColor?: string;
    height: string;
    background: string;
    showMetadataIcon?: boolean;
}

const CustomBarLabel = (props: any) => {
	const { x, y, width, height, value, payload } = props;

	if (payload?.isSpacer || value == null) return null;

	return (
		<text
		x={x + width + 6}
		y={y + height / 2}
		dy={3}
		fill="#666666"
		fontSize={9}
		textAnchor="start"
		>
		{formatValueBase(value, 'integer')}
		</text>
	);
};

const CustomTooltipBar = ({ active, payload, label }: any) => {
	if (!active || !payload || !payload.length) return null;

	const barData = payload[0]?.payload;

	if (barData?.isSpacer) return null;

	return (
		<div className="bg-white p-2 border border-slate-200 rounded shadow-sm text-xs">
		<p className="font-semibold">{`${label.replace(/\bNH\b/, "Non-Hispanic")}`}</p>
		<p>{`Count: ${formatValueBase(barData.barLabel, "integer")}`}</p>
		<p>{`Share: ${formatValue(payload[0].value, barData.unit)}`}</p>
		</div>
	);
};

const CustomTick = ({ x, y, payload }: any) => {
	const maxLineLength = 25;
	const label = typeof payload.value === "string" ? payload.value : String(payload.value);

	if (label.trim() === "") return null; // don't render tick for spacer

	const words = label.split(" ");
	const lines: string[] = [];
	let currentLine = "";

	words.forEach((word: any) => {
		if ((currentLine + word).length <= maxLineLength) {
		currentLine += (currentLine ? " " : "") + word;
		} else {
		if (currentLine) lines.push(currentLine);
		currentLine = word;
		}
	});
	if (currentLine) lines.push(currentLine);

	if (lines.length === 1) {
		return (
		<text
			x={x}
			y={y}
			dy={2}
			textAnchor="end"
			fill="#666"
			style={{ whiteSpace: "nowrap", fontSize: 9 }}
		>
			{lines[0]}
		</text>
		);
	}

	return (
		<text x={x} y={y} textAnchor="end" fill="#666" style={{ fontSize: 9 }}>
		{lines.map((line, i) => (
			<tspan key={i} x={x} dy={i === 0 ? -1 : 9}>
			{line}
			</tspan>
		))}
		</text>
	);
};

const RaceEthnicityBarChartCard = ({
	raceMetric,
	disaggData,
	geoDetails,
	geoTag,
	selectionActive,
	ethnicityMetric,
	height,
	background,
	barColor = "#337AB7",
	showMetadataIcon = true
	}: RaceEthnicityBarChartCardProps) => {
	if (
		!raceMetric ||
		!raceMetric.versions ||
		raceMetric.versions.length === 0 ||
		!raceMetric.versions[0].disaggregations
	) {
		return (
		<div className="h-24 flex items-center justify-center text-xs text-slate-500">
			<Skeleton height={24} width={"100%"} />
		</div>
		);
	}

	const id = raceMetric.id;
	const unit = raceMetric.versions[0]?.unit;
	const name = raceMetric.name;

	const getSortedRaceData = (metric: DisaggregatedMetric | undefined) => {
		if (!metric?.versions?.[0].disaggregations) return [];
		return [...metric.versions[0].disaggregations]
		.sort((a, b) => b.value - a.value)
		.map((d) => ({
			name: d.name,
			value: d.value ?? 0,
			unit: unit,
			barLabel: d.percent_calc?.n ?? null,
		}));
	};

	const getEthnicityData = (metric: DisaggregatedMetric | undefined) => {
		const disaggregations = metric?.versions?.[0]?.disaggregations ?? [];

		const hispanic = disaggregations.find(d => d.name === "Hispanic or Latino");
  		if (!hispanic?.percent_calc) return [];

		return [
			{
			name: "Hispanic or Latino",
			value: hispanic.percent_calc.n / hispanic.percent_calc.d,
			unit,
			barLabel: hispanic.percent_calc.n,
			},
		];
	};

	const raceData = useMemo(() => getSortedRaceData(raceMetric), [raceMetric, unit]);
	const ethnicityData = useMemo(() => getEthnicityData(ethnicityMetric), [ethnicityMetric, unit]);

	// Insert a spacer between race and ethnicity bars
	const chartData = useMemo(
	() =>
		ethnicityData.length > 0
		? [
			...raceData,
			{ name: "", value: 0, unit, barLabel: null, isSpacer: true },
			...ethnicityData,
			]
		: raceData,
	[raceData, ethnicityData, unit]
	);

	if (chartData.length === 0) {
		return (
		<div className="h-24 flex items-center justify-center text-xs text-slate-500">
			No data available
		</div>
		);
	}

	return (
		<>
		<div className="relative overflow-hidden group hover:shadow-md transition-all border rounded-xl bg-white">
			<div className="p-4 pb-1">
				<div className="flex items-start justify-between mb-3">
					<h3 className="text-sm font-semibold text-port-text">
						{name}
					</h3>
					
					<MetricMetadataTrigger
						metricId={id}
						metricName={name}
						disaggData={disaggData}
						geoDetails={geoDetails}
						geoTag={geoTag}
						selectionActive={selectionActive}
						showIcon={showMetadataIcon}
					/>
				</div>
			</div>

			<div className={`${height} ${background}`}>
			<ResponsiveContainer width="100%" height="100%">
				<BarChart
				data={chartData}
				layout="vertical"
				margin={{ top: 2, right: 40, left: 40, bottom: 0 }}
				barCategoryGap="8%"
				barGap={-2}
				>
				<XAxis
					type="number"
					tick={{ fontSize: 9 }}
					tickFormatter={(value) => formatValue(value, unit, id)}
				/>
				<YAxis
					type="category"
					dataKey="name"
					tick={<CustomTick />}
					width={100}
					interval={0}
					tickLine={false}
				/>
				<Tooltip
					content={<CustomTooltipBar />}
					cursor={{ fill: "transparent" }}
				/>
				<Bar dataKey="value" fill={barColor} activeBar={false} radius={[0, 4, 4, 0]} barSize={14}>
					<LabelList dataKey="barLabel" content={<CustomBarLabel />} />
				</Bar>
				</BarChart>
			</ResponsiveContainer>
			</div>

			<div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#2258e5]/50 to-[#00ac4e]/50 opacity-0 group-hover:opacity-100 transition-opacity" />
		</div>
		</>
	);
};

export default RaceEthnicityBarChartCard;
